import { c as _c } from "react/compiler-runtime";
import { varAlpha } from 'minimal-shared/utils';
import SvgIcon from '@mui/material/SvgIcon';
import { alertClasses } from '@mui/material/Alert';

// ----------------------------------------------------------------------

/**
 * Icons
 * @see https://icon-sets.iconify.design/solar/info-circle-bold/
 */
const AlertInfoIcon = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path fill="currentColor" fillRule="evenodd" clipRule="evenodd" d="M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0m-10 5.8a1 1 0 0 0 .8-.8v-6a.8.8 0 0 0-1.6 0v6q.1.7.8.8M12 7a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <SvgIcon {...props}>{t0}</SvgIcon>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

/**
 * Icons
 * @see https://icon-sets.iconify.design/solar/check-circle-bold/
 */
const AlertSuccessIcon = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path fill="currentColor" fillRule="evenodd" clipRule="evenodd" d="M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0m-6-3a1 1 0 0 1 0 1l-5 5a1 1 0 0 1-1 0l-2-2a.8.8 0 1 1 1-1l1.5 1.4 2.2-2.2L15 9a1 1 0 0 1 1 0" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <SvgIcon {...props}>{t0}</SvgIcon>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

/**
 * Icons
 * @see https://icon-sets.iconify.design/solar/danger-triangle-bold/
 */
const AlertWarningIcon = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path fill="currentColor" fillRule="evenodd" clipRule="evenodd" d="M5.3 10.8C8.3 5.6 9.7 3 12 3s3.8 2.6 6.7 7.8l.4.6c2.4 4.3 3.6 6.5 2.5 8S17.8 21 12.4 21h-.8c-5.4 0-8.1 0-9.2-1.6s.1-3.7 2.5-8zM12 7.2a1 1 0 0 1 .8.8v5a.8.8 0 0 1-1.6 0V8a1 1 0 0 1 .8-.7m0 9.7a1 1 0 1 0 0-2 1 1 0 0 0 0 2" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <SvgIcon {...props}>{t0}</SvgIcon>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

/**
 * Icons
 * @see https://icon-sets.iconify.design/solar/danger-bold/
 */
const AlertErrorIcon = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path fill="currentColor" fillRule="evenodd" clipRule="evenodd" d="M7.8 3.8c2-1.2 3-1.8 4.2-1.8 1.1 0 2.1.6 4.2 1.8l.6.4c2 1.2 3 1.8 3.6 2.8s.6 2.2.6 4.6v.8c0 2.4 0 3.6-.6 4.6s-1.5 1.6-3.6 2.8l-.6.4c-2 1.2-3 1.8-4.2 1.8-1.1 0-2.1-.6-4.2-1.8l-.6-.4c-2-1.2-3-1.8-3.6-2.8S3 14.8 3 12.4v-.8C3 9.2 3 8 3.6 7S5 5.4 7.2 4.2zM13 16a1 1 0 1 1-2 0 1 1 0 0 1 2 0m-1-9.7a1 1 0 0 1 .8.7v6a.8.8 0 0 1-1.6 0V7a1 1 0 0 1 .8-.7" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <SvgIcon {...props}>{t0}</SvgIcon>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const COLORS = ['info', 'success', 'warning', 'error'];
function styleColors(ownerState, styles) {
  const outputStyle = COLORS.reduce((acc, color) => {
    if (ownerState.severity === color) {
      acc = styles(color);
    }
    return acc;
  }, {});
  return outputStyle;
}
const MuiAlert = {
  /** DEFAULT PROPS */
  defaultProps: {
    iconMapping: {
      error: <AlertErrorIcon />,
      info: <AlertInfoIcon />,
      success: <AlertSuccessIcon />,
      warning: <AlertWarningIcon />
    }
  },
  /** STYLE */
  styleOverrides: {
    icon: {
      opacity: 1
    },
    /** @variant standard */
    standard: ({
      ownerState,
      theme
    }) => {
      const styled = {
        colors: styleColors(ownerState, color => ({
          color: theme.vars.palette[color].darker,
          backgroundColor: theme.vars.palette[color].lighter,
          ...theme.applyStyles('dark', {
            color: theme.vars.palette[color].lighter,
            backgroundColor: theme.vars.palette[color].darker
          }),
          [`& .${alertClasses.icon}`]: {
            color: theme.vars.palette[color].main,
            ...theme.applyStyles('dark', {
              color: theme.vars.palette[color].light
            })
          }
        }))
      };
      return {
        ...styled.colors
      };
    },
    /** @variant filled */
    filled: ({
      ownerState,
      theme
    }) => {
      const styled = {
        colors: styleColors(ownerState, color => ({
          color: theme.vars.palette[color].contrastText
        }))
      };
      return {
        ...styled.colors
      };
    },
    /** @variant outlined */
    outlined: ({
      ownerState,
      theme
    }) => {
      const styled = {
        colors: styleColors(ownerState, color => ({
          backgroundColor: varAlpha(theme.vars.palette[color].mainChannel, 0.08),
          color: theme.vars.palette[color].dark,
          border: `solid 1px ${varAlpha(theme.vars.palette[color].mainChannel, 0.16)}`,
          ...theme.applyStyles('dark', {
            color: theme.vars.palette[color].light
          }),
          [`& .${alertClasses.icon}`]: {
            color: theme.vars.palette[color].main
          }
        }))
      };
      return {
        ...styled.colors
      };
    }
  }
};
const MuiAlertTitle = {
  /** STYLE */
  styleOverrides: {
    root: ({
      theme
    }) => ({
      marginBottom: theme.spacing(0.5),
      fontWeight: theme.typography.fontWeightSemiBold
    })
  }
};
export const alert = {
  MuiAlert,
  MuiAlertTitle
};